import React, { useState, useEffect, useCallback } from 'react';
import { MdDelete, MdNotifications, MdNotificationsOff } from 'react-icons/md';
import { useApiContext } from '../context/ApiContext';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import Spin from '../components/Spin';

const TAB_APARTMENTS = 'apartments';
const TAB_CONDITIONS = 'conditions';

/**
 * Summarize a filters object into a human-readable string.
 */
function summarizeFilters(filters) {
  if (!filters) return '';
  const parts = [];

  // Region/city info
  if (filters.selectedRegion) parts.push(filters.selectedRegion);
  if (filters.selectedCities && filters.selectedCities.length > 0) {
    parts.push(filters.selectedCities.join(', '));
  }
  // Fallback for flat-format filters
  if (filters.region) parts.push(filters.region);
  if (filters.district) parts.push(filters.district);

  // Area filter (nested object from FilterContext)
  const area = filters.areaFilter;
  if (area && (area.minArea || area.maxArea)) {
    parts.push(`${area.minArea ?? ''}~${area.maxArea ?? ''}\u33A1`);
  } else if (filters.minArea || filters.maxArea) {
    parts.push(`${filters.minArea ?? ''}~${filters.maxArea ?? ''}\u33A1`);
  }

  // Price filter (nested object from FilterContext)
  const price = filters.priceFilter;
  if (price && (price.minPrice || price.maxPrice)) {
    const min = price.minPrice ? `${(price.minPrice / 10000).toFixed(0)}\uC5B5` : '';
    const max = price.maxPrice ? `${(price.maxPrice / 10000).toFixed(0)}\uC5B5` : '';
    parts.push(`\uB9E4\uB9E4 ${min}~${max}`);
  } else if (filters.minPrice || filters.maxPrice) {
    const min = filters.minPrice ? `${(filters.minPrice / 10000).toFixed(0)}\uC5B5` : '';
    const max = filters.maxPrice ? `${(filters.maxPrice / 10000).toFixed(0)}\uC5B5` : '';
    parts.push(`\uB9E4\uB9E4 ${min}~${max}`);
  }

  // Gap filter
  const gap = filters.gapFilter;
  if (gap && (gap.minGap || gap.maxGap)) {
    parts.push(`\uAC2D ${gap.minGap ?? ''}~${gap.maxGap ?? ''}`);
  }

  return parts.join(' \u00B7 ') || '\uC804\uCCB4';
}

/**
 * Format a price number as Korean \uC5B5 notation.
 */
function formatPriceAsEok(price) {
  if (price == null) return '';
  const eok = Math.floor(price / 10000);
  const remainder = price % 10000;
  if (eok > 0 && remainder > 0) {
    return `${eok}\uC5B5 ${remainder.toLocaleString()}`;
  }
  if (eok > 0) {
    return `${eok}\uC5B5`;
  }
  return `${price.toLocaleString()}\uB9CC`;
}

function ApartmentCard({ item, onToggle, onDelete }) {
  return (
    <div className="bg-white rounded-lg shadow-md p-4 flex flex-col gap-2">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold truncate">
            {item.apartment_name}
          </h3>
          <p className="text-sm text-gray-500">
            {item.district} &middot; {item.area_size}{'\u33A1'}
          </p>
        </div>
        <div className="flex items-center gap-1 ml-2 shrink-0">
          <button
            onClick={() => onToggle(item)}
            className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
            aria-label={item.is_active ? '\uC54C\uB9BC \uB044\uAE30' : '\uC54C\uB9BC \uCF1C\uAE30'}
          >
            {item.is_active ? (
              <MdNotifications className="text-blue-500 text-xl" />
            ) : (
              <MdNotificationsOff className="text-gray-400 text-xl" />
            )}
          </button>
          <button
            onClick={() => onDelete(item)}
            className="p-1.5 rounded-md hover:bg-red-50 transition-colors"
            aria-label={'\uC0AD\uC81C'}
          >
            <MdDelete className="text-red-500 text-xl" />
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-0.5 text-sm">
        <span className="text-gray-700">
          {'\uD604\uC7AC \uB9E4\uB9E4\uAC00'}: <strong>{formatPriceAsEok(item.current_price)}</strong>
        </span>
        {item.target_price != null && (
          <span className="text-gray-500">
            {'\uBAA9\uD45C\uAC00'}: {formatPriceAsEok(item.target_price)}
          </span>
        )}
      </div>
    </div>
  );
}

function ConditionCard({ item, onToggle, onDelete }) {
  return (
    <div className="bg-white rounded-lg shadow-md p-4 flex flex-col gap-2">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold truncate">{item.name}</h3>
          <p className="text-sm text-gray-500">{summarizeFilters(item.filters)}</p>
        </div>
        <div className="flex items-center gap-1 ml-2 shrink-0">
          <button
            onClick={() => onToggle(item)}
            className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
            aria-label={item.is_active ? '\uC54C\uB9BC \uB044\uAE30' : '\uC54C\uB9BC \uCF1C\uAE30'}
          >
            {item.is_active ? (
              <MdNotifications className="text-blue-500 text-xl" />
            ) : (
              <MdNotificationsOff className="text-gray-400 text-xl" />
            )}
          </button>
          <button
            onClick={() => onDelete(item)}
            className="p-1.5 rounded-md hover:bg-red-50 transition-colors"
            aria-label={'\uC0AD\uC81C'}
          >
            <MdDelete className="text-red-500 text-xl" />
          </button>
        </div>
      </div>

      <p className="text-sm text-gray-700">
        {'\uB9E4\uCE6D \uACB0\uACFC'}: <strong>{item.match_count}{'\uAC74'}</strong>
      </p>
    </div>
  );
}

export default function Watchlist({ session }) {
  const { api } = useApiContext();
  const { loading: subLoading, hasAccess } = useSubscriptionAccess(session);

  const [activeTab, setActiveTab] = useState(TAB_APARTMENTS);
  const [watchlist, setWatchlist] = useState({ apartments: [], conditions: [] });
  const [loading, setLoading] = useState(false);

  const fetchWatchlist = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await api.getWatchlist();
      if (error) {
        return;
      }
      setWatchlist(data);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (hasAccess) {
      fetchWatchlist();
    }
  }, [hasAccess, fetchWatchlist]);

  const handleToggle = async (item) => {
    const { error } = await api.updateWatchlistItem(item.id, { is_active: !item.is_active });
    if (!error) fetchWatchlist();
  };

  const handleDelete = async (item) => {
    const { error } = await api.deleteWatchlistItem(item.id);
    if (!error) fetchWatchlist();
  };

  if (subLoading) {
    return (
      <div className="flex justify-center items-center p-8 h-[60vh]">
        <Spin size="large" />
        <span className="ml-3 text-lg text-gray-700">{'\uB85C\uB529 \uC911...'}</span>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="flex flex-col justify-center items-center p-4 h-[60vh]">
        <h2 className="text-2xl font-bold mb-4">
          {'\uC774 \uAE30\uB2A5\uC744 \uC0AC\uC6A9\uD558\uB824\uBA74 \uAD6C\uB3C5\uC774 \uD544\uC694\uD569\uB2C8\uB2E4'}
        </h2>
        <p className="text-gray-600 mb-6 text-center">
          {'\uAD00\uC2EC \uBAA9\uB85D \uAE30\uB2A5\uC740 \uC720\uB8CC \uAD6C\uB3C5 \uD68C\uC6D0 \uB610\uB294 \uBB34\uB8CC\uCCB4\uD5D8 \uAE30\uAC04 \uC911\uC5D0\uB9CC \uC774\uC6A9\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.'}
        </p>
      </div>
    );
  }

  const apartments = watchlist.apartments ?? [];
  const conditions = watchlist.conditions ?? [];

  const tabItems = [
    { key: TAB_APARTMENTS, label: '\uCC1C\uD55C \uB2E8\uC9C0' },
    { key: TAB_CONDITIONS, label: '\uC800\uC7A5\uD55C \uC870\uAC74' },
  ];

  return (
    <div className="flex flex-col items-center p-4 md:p-8 max-w-3xl mx-auto">
      <h1 className="text-2xl md:text-3xl font-bold mb-6">{'\uAD00\uC2EC \uBAA9\uB85D'}</h1>

      {/* Tabs */}
      <div className="flex w-full border-b mb-6" role="tablist">
        {tabItems.map(({ key, label }) => (
          <button
            key={key}
            role="tab"
            aria-selected={activeTab === key}
            onClick={() => setActiveTab(key)}
            className={`flex-1 py-3 text-center font-medium transition-colors ${
              activeTab === key
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Spin size="default" />
          <span className="ml-3 text-gray-600">{'\uBD88\uB7EC\uC624\uB294 \uC911...'}</span>
        </div>
      ) : (
        <div className="w-full flex flex-col gap-4">
          {activeTab === TAB_APARTMENTS &&
            (apartments.length === 0 ? (
              <p className="text-center text-gray-500 py-12">
                {'\uCC1C\uD55C \uB2E8\uC9C0\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4.'}
              </p>
            ) : (
              apartments.map((item) => (
                <ApartmentCard
                  key={item.id}
                  item={item}
                  onToggle={handleToggle}
                  onDelete={handleDelete}
                />
              ))
            ))}

          {activeTab === TAB_CONDITIONS &&
            (conditions.length === 0 ? (
              <p className="text-center text-gray-500 py-12">
                {'\uC800\uC7A5\uD55C \uC870\uAC74\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.'}
              </p>
            ) : (
              conditions.map((item) => (
                <ConditionCard
                  key={item.id}
                  item={item}
                  onToggle={handleToggle}
                  onDelete={handleDelete}
                />
              ))
            ))}
        </div>
      )}
    </div>
  );
}
